import React, { useState } from "react";
import { Button, Collapse } from "react-bootstrap";
import { FiChevronDown, FiChevronUp } from "react-icons/fi";

import { ReactComponent as MapPin } from "../../assets/images/map-pin-icon.svg";
import { ReactComponent as Clock } from "../../assets/images/clock.svg";
import { Copy, Send, Users } from "lucide-react";
import { toast } from "sonner";
import CreatePostModal from "../community/components/create-post-modal/CreatePostModal";
import useCategories from "../community/hooks/useCategories";
import usePosts from "../community/hooks/usePosts";

import { useDispatch } from "react-redux";
import { applyJob } from "../../redux/slices/jobsSlice";
import {
  FaFacebookF,
  FaLinkedinIn,
  FaWhatsapp,
  FaXTwitter,
} from "react-icons/fa6";
import { useNavigate } from "react-router-dom";

const Jobcards = ({ job, isSaved, onToggleSave, onApply }) => {
  const dispatch = useDispatch();
  const hasJobMeta = Boolean(job?.promoted_by || job?.source_name);
  const navigate = useNavigate();

  const [shareModal, setShareModal] = useState(false);
  const [showCreatePostModal, setShowCreatePostModal] = useState(false);
  const [preFilledContent, setPreFilledContent] = useState("");
  const [isExpanded, setIsExpanded] = useState(false);
  const [avatarImgError, setAvatarImgError] = useState(false);
  // useCategories / usePosts moved into <LazyCreatePostModal> below so the
  // categories API is only called when the user actually opens the modal.

  const formatSalary = (salary) => {
    if (!salary || (!salary.min && !salary.max)) {
      return "Salary not listed";
    }

    const min = salary.min ? Number(salary.min).toLocaleString() : null;

    const max = salary.max ? Number(salary.max).toLocaleString() : null;

    if (min && max) {
      return `$${min} - $${max}/${salary.period}`;
    }

    if (min) {
      return `From $${min}/${salary.period}`;
    }

    if (max) {
      return `Up to $${max}/${salary.period}`;
    }

    return "Compensation not disclosed";
  };

  const formatbonus = (bonus) => {
    if (!bonus) {
      return "Not listed";
    }

    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      // Optional: Use 'narrowSymbol' to ensure it just shows $
      currencyDisplay: "symbol",
      // Usually, salaries don't show cents. You can toggle this:
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(bonus);
  };

  // Generate formatted job content for community post
  const generateJobPostContent = () => {
    const jobTitle = job?.title || "Job Opportunity";
    const companyName = job?.company?.name || "Company";
    const salary = formatSalary(job?.salary);
    const location =
      job?.location?.city && job?.location?.state
        ? `${job.location.city}, ${job.location.state}`
        : "Location not specified";
    const applicationUrl = job?.application_url || "";
    const websiteUrl = `${window.location.origin}/jobs`;

    let content = ` Job Opportunity: ${jobTitle}\n\n`;
    content += ` Company: ${companyName}\n`;
    content += ` Salary: ${salary}\n`;
    content += ` Location: ${location}\n`;

    if (job?.work_model) {
      content += ` Work Mode: ${job.work_model}\n`;
    }
    if (job?.job_type) {
      content += ` Job Type: ${job.job_type}\n`;
    }
    if (job?.shift_type) {
      content += ` Shift: ${job.shift_type}\n`;
    }

    if (job?.description) {
      content += `\n Description:\n${job.description}\n`;
    }

    if (applicationUrl) {
      content += `\n Apply here: ${applicationUrl}\n`;
    }

    content += `\n For more job opportunities, visit: ${websiteUrl}`;

    return content;
  };

  const timeAgo = (date) => {
    if (!date) return "";

    const now = Date.now();
    const past = new Date(date).getTime();
    const diffMs = now - past;

    const seconds = Math.floor(diffMs / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    const months = Math.floor(days / 30);
    const years = Math.floor(days / 365);

    // Just now
    if (seconds < 60) return "Just now";

    // Minutes
    if (minutes < 60) return minutes === 1 ? "1 min ago" : `${minutes} min ago`;

    // Hours
    if (hours < 24) return hours === 1 ? "1 hour ago" : `${hours} hours ago`;

    // Weeks (ONLY exact multiples, up to 4 weeks)
    if (days >= 7 && days <= 28 && days % 7 === 0) {
      const weeks = days / 7;
      return weeks === 1 ? "1 week ago" : `${weeks} weeks ago`;
    }

    // Days (everything else under 30)
    if (days < 30) return days === 1 ? "1 day ago" : `${days} days ago`;

    // Months
    if (months < 12)
      return months === 1 ? "1 month ago" : `${months} months ago`;

    // Years
    return years === 1 ? "1 year ago" : `${years} years ago`;
  };
  const getApplyUrl = (url) => {
    if (!url) return "#";

    // If already has http or https, return as is
    if (/^https?:\/\//i.test(url)) {
      return url;
    }

    // Otherwise, prepend https
    return `https://${url}`;
  };

  const handleShareClick = () => {
    setShareModal(true);
  };

  const closeShareModal = () => {
    setShareModal(false);
  };

  const createSlug = (text = "") => {
    return text
      .toString()
      .toLowerCase()
      .trim()
      .replace(/&/g, "and")
      .replace(/[^a-z0-9\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "");
  };

  const buildJobSeoUrl = (jobData) => {
    const titleSlug = createSlug(jobData?.title);
    return titleSlug
      ? `/job-detail/${titleSlug}-${jobData?.id}`
      : `/job-detail/${jobData?.id}`;
  };

  const shareUrl = () => {
    return `${window.location.origin}${buildJobSeoUrl(job)}`;
  };

  const shareToSocialMedia = (platform) => {
    // Share an internal MapMyPay job-detail link (SEO-friendly URL)
    const applicationUrl = shareUrl();

    const jobTitle = job?.title || "this job opportunity";
    const companyName = job?.company?.name || "Company";

    const shareText = `Check out ${jobTitle} at ${companyName}`;

    let url = "";
    switch (platform) {
      case "twitter":
        url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
          shareText,
        )}&url=${encodeURIComponent(applicationUrl)}`;
        break;
      case "facebook":
        url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
          applicationUrl,
        )}`;
        break;
      case "whatsapp":
        url = `https://wa.me/?text=${encodeURIComponent(
          shareText + " " + applicationUrl,
        )}`;
        break;
      case "linkedin":
        url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
          applicationUrl,
        )}`;
        break;
      case "copy":
        navigator.clipboard
          .writeText(applicationUrl)
          .then(() => {
            toast.success("Link copied to clipboard!");
          })
          .catch(() => {
            toast.error("Failed to copy link");
          });
        closeShareModal();
        return;
      default:
        return;
    }

    if (url) {
      const popup = window.open(
        url,
        "_blank",
        "width=600,height=400,scrollbars=yes,resizable=yes",
      );
      if (!popup) {
        toast.warning("Please allow popups for this site to share content");
      }
      closeShareModal();
    }
  };

  const avatarColors = [
    // '0195D3',
    // '16A34A',
    // 'DC2626',
    // '7C3AED',
    // 'EA580C',
    // '0D9488',
    // 'CA8A04',
    "054473",
    "0195D3",
    "7c3aed",
  ];

  const getAvatarColor = (key) => {
    let hash = 0;
    const str = String(key || "Company");

    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }

    return avatarColors[Math.abs(hash) % avatarColors.length];
  };
  const avatarBg = getAvatarColor(job?.id || job?.company?.name);

  // ── New compact card helpers ──────────────────────────────
  const getInitials = (name) => {
    if (!name) return "CO";
    const words = String(name)
      .trim()
      .split(/\s+/);
    const letters = words
      .map((w) => w[0])
      .join("")
      .slice(0, 2)
      .toUpperCase();
    return letters || "CO";
  };

  const initials = getInitials(job?.company_name || job?.company?.name);
  const initialsBg = `#${avatarBg}`;
  const compactLocation =
    job?.location?.city && job?.location?.state
      ? `${job.location.city}, ${job.location.state}`
      : job?.location?.city || job?.location?.state || "Location not specified";
  const isHospital = job?.source === "hospital";
  const compactSalary = formatSalary(job?.salary);

  // Split formatted salary into amount + period so each can be colored
  // independently ("$92 - $134" in brand color, "/hourly" in gray).
  const splitSalary = (str) => {
    if (
      !str ||
      str === "Salary not listed" ||
      str === "Compensation not disclosed"
    ) {
      return { amount: str, period: "" };
    }
    const idx = str.lastIndexOf("/");
    if (idx === -1) return { amount: str, period: "" };
    return { amount: str.slice(0, idx), period: str.slice(idx) };
  };
  const salaryParts = splitSalary(compactSalary);

  return (
    <>
      {/* ── NEW COMPACT JOB CARD (matches Figma "Jobs for this city") ── */}
      <div className="jc-new">
        <button
          type="button"
          className="jc-new__header"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div
            className="jc-new__avatar"
            style={{ background: initialsBg }}
            aria-hidden="true"
          >
            {job?.profile_photo && !avatarImgError ? (
              <img
                src={
                  job?.profile_photo.startsWith("http")
                    ? job?.profile_photo
                    : `${process.env.REACT_APP_API_BASE_URL}/uploads/job_photos/${job?.profile_photo}`
                }
                alt={job?.company?.name || "Company"}
                onError={() => setAvatarImgError(true)}
              />
            ) : (
              <span>{initials}</span>
            )}
          </div>

          <div className="jc-new__main">
            <div className="jc-new__company">
              {job?.company_name || job?.company?.name || "Company"}
            </div>
            <div className="jc-new__title">{job?.title}</div>
            <div className="jc-new__loc-row">
              <MapPin className="jc-new__pin" />
              <span className="jc-new__loc">{compactLocation}</span>
              {isHospital && <span className="jc-new__badge">HOSPITAL</span>}
            </div>
            {compactSalary && compactSalary !== "Salary not listed" && (
              <div className="jc-new__pay">
                <span className="jc-new__pay-amount">{salaryParts.amount}</span>
                {salaryParts.period && (
                  <span className="jc-new__pay-period">
                    {salaryParts.period}
                  </span>
                )}
              </div>
            )}
          </div>

          <FiChevronDown
            className={`jc-new__chev ${isExpanded ? "is-open" : ""}`}
            size={16}
          />
        </button>

        <Collapse in={isExpanded}>
          <div>
            <div className="jc-new__expanded">
              {(job?.work_model || job?.shift_type || job?.job_type) && (
                <div className="jc-new__tags">
                  {job?.work_model && (
                    <span className="jc-new__tag">{job.work_model}</span>
                  )}
                  {job?.shift_type && (
                    <span className="jc-new__tag">{job.shift_type}</span>
                  )}
                  {job?.job_type && (
                    <span className="jc-new__tag">{job.job_type}</span>
                  )}
                </div>
              )}
              <Button
                type="button"
                className="jc-new__apply"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (!job?.id) return;
                  if (onApply) {
                    onApply(job.id);
                  } else {
                    navigate(`/job-detail/${job.id}`);
                  }
                }}
              >
                Apply Now
              </Button>
            </div>
          </div>
        </Collapse>
      </div>

      {/* ── OLD CARD kept for reference / easy revert (wrapped in `false &&` so it never renders) ── */}
      {false && (
        <div className="contentwrap">
          <div
            className={`job-card-header ${
              !hasJobMeta ? "job-card-header--no-meta" : ""
            }`}
            onClick={() => setIsExpanded(!isExpanded)}
            style={{ cursor: "pointer" }}
          >
            <div className="job-icon">
              <img
                // src={
                //   job?.profile_photo
                //     ? `${process.env.REACT_APP_API_BASE_URL}/uploads/job_photos/${job.profile_photo}`
                //     : `https://ui-avatars.com/api/?name=${encodeURIComponent(
                //         job?.company?.name || "Company",
                //       )}&background=${avatarBg}&color=fff&size=100&bold=true`
                // }
                src={
                  job?.profile_photo
                    ? job?.profile_photo.startsWith("http")
                      ? job?.profile_photo // ✅ already full URL → use as is
                      : `${process.env.REACT_APP_API_BASE_URL}/uploads/job_photos/${job?.profile_photo}` // ✅ local path
                    : `https://ui-avatars.com/api/?name=${encodeURIComponent(
                        job?.company_name || job?.company?.name || "Company",
                      )}&background=${avatarBg}&color=fff&size=100&bold=true`
                }
                alt={job?.company?.name || "Company"}
                className="user-avatar"
                onError={(e) => {
                  const target = e.target;
                  target.onerror = null;
                  target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                    job?.company?.name || "Company",
                  )}&background=${avatarBg}&color=fff&size=100&bold=true`;
                }}
              />
            </div>

            <div className="job-title-description">
              <div className="title-popular">
                <div className="d-flex align-items-center justify-content-between">
                  <div className="job-title">{job?.company?.name}</div>
                  {/* <div className="d-flex align-items-center gap-2 badge-container">
                  {job?.source === "hospital" && (
                    <span
                      className={`badge ${job?.apply_count > 5
                        ? "hospital-badge hospital-with-popular"
                        : "hospital-badge hospital-alone"
                        }`}
                    >
                      Hospital
                    </span>
                  )}

                  {job?.apply_count > 5 && (
                    <span className="badge populars">Popular</span>
                  )}


                </div> */}
                </div>
                {/* {job?.apply_count > 5 && <div className="popular">Popular</div>} */}
                <div className="job-type">{job?.title}</div>
                <div className="job-location">
                  <MapPin size={14} />
                  {job?.location?.city && job?.location?.state
                    ? `${job.location.city}, ${job.location.state}`
                    : "Location not specified"}
                </div>
                <div className="job-salary"> {formatSalary(job?.salary)}</div>
              </div>

              <div className="job-description">
                {job.promoted_by && (
                  <div className="tag">
                    <div className="tag-desc">
                      Promoted by {job.promoted_by}
                    </div>
                  </div>
                )}
                {job?.source_name && (
                  <span className="job-desc-text">
                    Responses managed off {job?.source_name}
                  </span>
                )}
              </div>
            </div>

            {/* Accordion Toggle Icon */}
            <div className="accordion-btn">
              {isExpanded ? (
                <FiChevronUp size={20} />
              ) : (
                <FiChevronDown size={20} />
              )}
            </div>
          </div>

          {/* Collapsible Content */}
          <Collapse in={isExpanded}>
            <div>
              <div className="content">
                <div className="job-meta">
                  <span className="posted-time">
                    {job?.apply_count} people clicked apply
                  </span>
                  <span className="job-schedule">
                    <Clock size={14} />
                    <span>{timeAgo(job?.posted_date)} </span>
                  </span>
                </div>

                <div className="job-desc">{job?.description}</div>

                <div className="job-meta">
                  <div className="job-bonus">
                    <span>Bonus : </span>
                    {formatbonus(job?.bonus)}
                  </div>

                  {job?.min_education && (
                    <div className="job-bonus">
                      <span>Min Education : </span>
                      {job?.min_education}
                    </div>
                  )}
                </div>
                <div className="job-meta">
                  {job?.required_license_type && (
                    <div className="job-bonus">
                      <span>Required License : </span>
                      {job?.required_license_type}
                    </div>
                  )}
                  {job?.min_years_experience && (
                    <div className="job-bonus">
                      <span>Min Years Experience : </span>
                      {job?.min_years_experience}
                    </div>
                  )}
                </div>
                <div className="job-meta">
                  {job?.specialty && (
                    <div className="job-bonus">
                      <span>Specialty : </span>
                      {job?.specialty}
                    </div>
                  )}

                  {job?.certifications_required && (
                    <div className="job-bonus">
                      <span>Required Certificate : </span>
                      {(() => {
                        try {
                          const value = job.certifications_required;

                          if (Array.isArray(value)) {
                            return value.join(", ");
                          }

                          const parsed = JSON.parse(value || "[]");

                          return Array.isArray(parsed)
                            ? parsed.join(", ")
                            : String(parsed);
                        } catch (e) {
                          return String(job.certifications_required);
                        }
                      })()}
                    </div>
                  )}
                </div>

                <div className="job-schedule tags">
                  {job?.work_model && (
                    <div className="tag">
                      <div className="tag-desc">{job.work_model}</div>
                    </div>
                  )}
                  {job?.shift_type && (
                    <div className="tag">
                      <div className="tag-desc">{job.shift_type}</div>
                    </div>
                  )}

                  {job?.job_type && (
                    <div className="tag">
                      <div className="tag-desc">{job.job_type}</div>
                    </div>
                  )}
                </div>
              </div>

              <div className="buttons">
                {/* Apply Button */}
                <Button
                  as="a"
                  href={getApplyUrl(job?.application_url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-100 btn btn-secondary"
                  onClick={() => {
                    if (!job?.id) return;

                    navigate(`/job-detail/${job.id}`);
                  }}
                  // onClick={() => {
                  //   if (!job?.id) return;

                  //   // If hospital job → go to internal detail page
                  //   if (job?.source === "hospital") {
                  //     navigate(`/job-detail/${job.id}`);
                  //     return;
                  //   }

                  //   // Otherwise call apply API
                  //   dispatch(applyJob(job.id));
                  // }}
                >
                  Apply Now
                </Button>
                <Button
                  variant="link"
                  size="sm"
                  onClick={handleShareClick}
                  className="d-flex align-items-center gap-2 action-btn text-muted btn btn-secondary"
                >
                  <Send size={24} />
                  {/* <span>Share</span> */}
                </Button>
                {shareModal && (
                  <div
                    className="modal-backdrop-overlay"
                    style={{
                      position: "fixed",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: "rgba(0, 0, 0, 0.6)",
                      backdropFilter: "blur(8px)",
                      WebkitBackdropFilter: "blur(8px)",
                      zIndex: 99998,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                    onClick={closeShareModal}
                  >
                    <div
                      className="modal-content-wrapper"
                      style={{
                        backgroundColor: "white",
                        borderRadius: "12px",
                        maxWidth: "400px",
                        width: "90%",
                        boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)",
                        zIndex: 99999,
                        position: "relative",
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {/* Modal Header */}
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          padding: "12px",
                          borderBottom: "1px solid #e5e7eb",
                        }}
                      >
                        <h3
                          style={{
                            fontSize: "18px",
                            fontWeight: "600",
                            color: "#111827",
                            margin: 0,
                            lineHeight: "24px",
                          }}
                        >
                          Share Job
                        </h3>
                        <button
                          onClick={closeShareModal}
                          style={{
                            background: "transparent",
                            border: "none",
                            cursor: "pointer",
                            padding: "4px",
                            borderRadius: "4px",
                            color: "#6b7280",
                            fontSize: "24px",
                            lineHeight: 1,
                          }}
                          aria-label="Close"
                        >
                          ×
                        </button>
                      </div>

                      {/* Modal Body */}
                      <div style={{ padding: "0" }}>
                        {/* Twitter */}
                        <div
                          onClick={() => shareToSocialMedia("twitter")}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "12px",
                            padding: "12px 24px",
                            cursor: "pointer",
                            borderBottom: "1px solid #f3f4f6",
                            transition: "background-color 0.2s ease",
                          }}
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.backgroundColor = "#f9fafb")
                          }
                          onMouseLeave={(e) =>
                            (e.currentTarget.style.backgroundColor =
                              "transparent")
                          }
                        >
                          <div
                            style={{
                              width: "22px",
                              height: "22px",
                              backgroundColor: "#1da1f2",
                              borderRadius: "4px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "white",
                              fontSize: "12px",
                              fontWeight: "bold",
                            }}
                          >
                            <FaXTwitter
                              size={14}
                              style={{
                                display: "block",
                                margin: "0 auto",
                              }}
                            />
                          </div>
                          <span style={{ fontSize: "14px", fontWeight: "500" }}>
                            Share on Twitter
                          </span>
                        </div>

                        {/* Facebook */}
                        <div
                          onClick={() => shareToSocialMedia("facebook")}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "12px",
                            padding: "12px 24px",
                            cursor: "pointer",
                            borderBottom: "1px solid #f3f4f6",
                            transition: "background-color 0.2s ease",
                          }}
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.backgroundColor = "#f9fafb")
                          }
                          onMouseLeave={(e) =>
                            (e.currentTarget.style.backgroundColor =
                              "transparent")
                          }
                        >
                          <div
                            style={{
                              width: "22px",
                              height: "22px",
                              backgroundColor: "#1877f2",
                              borderRadius: "4px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "white",
                              fontSize: "10px",
                              fontWeight: "bold",
                            }}
                          >
                            <FaFacebookF
                              size={14}
                              style={{
                                display: "block",
                                margin: "0 auto",
                              }}
                            />
                          </div>
                          <span style={{ fontSize: "14px", fontWeight: "500" }}>
                            Share on Facebook
                          </span>
                        </div>

                        {/* WhatsApp */}
                        <div
                          onClick={() => shareToSocialMedia("whatsapp")}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "12px",
                            padding: "12px 24px",
                            cursor: "pointer",
                            borderBottom: "1px solid #f3f4f6",
                            transition: "background-color 0.2s ease",
                          }}
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.backgroundColor = "#f9fafb")
                          }
                          onMouseLeave={(e) =>
                            (e.currentTarget.style.backgroundColor =
                              "transparent")
                          }
                        >
                          <div
                            style={{
                              width: "22px",
                              height: "22px",
                              backgroundColor: "#25d366",
                              borderRadius: "4px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "white",
                              fontSize: "12px",
                              fontWeight: "bold",
                            }}
                          >
                            <FaWhatsapp
                              size={14}
                              style={{
                                display: "block",
                                margin: "0 auto",
                              }}
                            />
                          </div>
                          <span style={{ fontSize: "14px", fontWeight: "500" }}>
                            Share on WhatsApp
                          </span>
                        </div>

                        {/* LinkedIn */}
                        <div
                          onClick={() => shareToSocialMedia("linkedin")}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "12px",
                            padding: "12px 24px",
                            cursor: "pointer",
                            borderBottom: "1px solid #f3f4f6",
                            transition: "background-color 0.2s ease",
                          }}
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.backgroundColor = "#f9fafb")
                          }
                          onMouseLeave={(e) =>
                            (e.currentTarget.style.backgroundColor =
                              "transparent")
                          }
                        >
                          <div
                            style={{
                              width: "22px",
                              height: "22px",
                              backgroundColor: "#0077b5",
                              borderRadius: "4px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "white",
                              fontSize: "10px",
                              fontWeight: "bold",
                            }}
                          >
                            <FaLinkedinIn
                              size={14}
                              style={{
                                display: "block",
                                margin: "0 auto",
                              }}
                            />
                          </div>
                          <span style={{ fontSize: "14px", fontWeight: "500" }}>
                            Share on LinkedIn
                          </span>
                        </div>

                        {/* Copy Link */}
                        <div
                          onClick={() => shareToSocialMedia("copy")}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "12px",
                            padding: "12px 24px",
                            cursor: "pointer",
                            transition: "background-color 0.2s ease",
                            borderBottomLeftRadius: "12px",
                            borderBottomRightRadius: "12px",
                          }}
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.backgroundColor = "#f9fafb")
                          }
                          onMouseLeave={(e) =>
                            (e.currentTarget.style.backgroundColor =
                              "transparent")
                          }
                        >
                          <Copy
                            style={{
                              width: "20px",
                              height: "20px",
                              color: "#6b7280",
                            }}
                          />
                          <span style={{ fontSize: "14px", fontWeight: "500" }}>
                            Copy Link
                          </span>
                        </div>

                        {/* Share to Community */}
                        <div
                          onClick={() => {
                            closeShareModal();
                            const jobContent = generateJobPostContent();
                            setPreFilledContent(jobContent);
                            setShowCreatePostModal(true);
                          }}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "12px",
                            padding: "12px 24px",
                            cursor: "pointer",
                            borderTop: "1px solid #f3f4f6",
                            transition: "background-color 0.2s ease",
                            borderBottomLeftRadius: "12px",
                            borderBottomRightRadius: "12px",
                          }}
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.backgroundColor = "#f0f9ff")
                          }
                          onMouseLeave={(e) =>
                            (e.currentTarget.style.backgroundColor =
                              "transparent")
                          }
                        >
                          <Users
                            style={{
                              width: "20px",
                              height: "20px",
                              color: "#0195d3",
                            }}
                          />
                          <span
                            style={{
                              fontSize: "14px",
                              fontWeight: "500",
                              color: "#0195d3",
                            }}
                          >
                            Share to Community
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Create Post Modal — mounted only when opened so that
                    useCategories/usePosts inside don't fire on every card mount. */}
                {showCreatePostModal && (
                  <LazyCreatePostModal
                    onHide={() => {
                      setShowCreatePostModal(false);
                      setPreFilledContent("");
                    }}
                    preFilledContent={preFilledContent}
                  />
                )}
              </div>
            </div>
          </Collapse>
        </div>
      )}
      {/* ── END OLD CARD ── */}
    </>
  );
};

/**
 * Lazy modal wrapper — useCategories runs in here, so it only fires the API
 * when this component is mounted (i.e. when the user clicks "Share to Community").
 */
const LazyCreatePostModal = ({ onHide, preFilledContent }) => {
  const { categories, loading: categoriesLoading } = useCategories();
  const { createPost } = usePosts();

  const jobListingCategory = React.useMemo(() => {
    if (!categories || categories.length === 0) return null;
    return categories.find((cat) => cat.id === 9) || null;
  }, [categories]);

  return (
    <CreatePostModal
      show={true}
      onHide={onHide}
      createPost={createPost}
      categories={categories}
      isLoading={categoriesLoading}
      initialCategory={jobListingCategory?.category_name || "Job Listing"}
      preFilledContent={preFilledContent}
      onpostSubmit={() => {}}
      onCategoryChange={() => {}}
    />
  );
};

export default Jobcards;
